import React from 'react';
import { AnimatePresence, motion, Reorder } from 'framer-motion';
import { ArrowDown, ArrowUp, GripVertical, Layers, Share2, Sparkles, X } from 'lucide-react';
import { SelectDropdown } from '../SelectDropdown';
import { AudienceLevel, LEVEL_LABELS } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
import { PathwayButton } from './PathwayButton';
interface PathwayTrayProps {
  name: string;
  /** Prospect or company this pathway is for — appears on the PDF cover. */
  company: string;
  /** True when the company shown was picked up from the conversation notes. */
  companyFromNotes: boolean;
  /** Module ids in the order the prospect should take them. */
  items: string[];
  audience: AudienceLevel | 'any';
  /** Set once the rep has chosen how edits to a saved pathway should land. */
  editContext: string | null;
  onRevert: () => void;
  onNameChange: (name: string) => void;
  onCompanyChange: (company: string) => void;
  onAudienceChange: (audience: AudienceLevel | 'any') => void;
  onRemove: (moduleId: string) => void;
  onMove: (moduleId: string, direction: -1 | 1) => void;
  onReorder: (moduleIds: string[]) => void;
  onClear: () => void;
  onShare: () => void;
}
export function PathwayTray({
  name,
  company,
  companyFromNotes,
  items,
  audience,
  editContext,
  onRevert,
  onNameChange,
  onCompanyChange,
  onAudienceChange,
  onRemove,
  onMove,
  onReorder,
  onClear,
  onShare
}: PathwayTrayProps) {
  // Company and pathway name both appear on the document a prospect reads, so
  // neither can be missing by the time it is generated.
  const missing: string[] = [];
  if (items.length === 0) missing.push('at least one module');
  if (!company.trim()) missing.push('a company');
  if (!name.trim()) missing.push('a pathway name');
  const canSave = missing.length === 0;
  const blockedReason = canSave ? null : `Add ${missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`} to save this pathway.`;
  return <aside aria-label="Your pathway" className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm lg:sticky lg:top-6" data-id="element-1823" data-mp="hjph0709ug88">
      <AnimatePresence initial={false} data-id="element-1824" data-mp="rhvjoqtwjqe5">
        {editContext && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden bg-[#30718D]/5" data-id="element-1825" data-mp="wgom9o7nhwzj">
            <div className="flex items-start gap-2 px-5 py-3" data-id="element-1826" data-mp="1a83f0mrd1cjs">
              <p className="flex-1 text-xs font-medium text-[#30718D]" data-id="element-1827" data-mp="1a7bk7e5l7iuk">
                {editContext}
              </p>
              <button type="button" onClick={onRevert} className="flex-shrink-0 text-xs font-medium text-gray-500 transition-colors hover:text-gray-900" data-id="element-1828" data-mp="1f5d23vra1c4g">
                Revert
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="border-b border-gray-100 p-5" data-id="element-1829" data-mp="2upm63eigvmzy">
        <div className="flex items-center justify-between" data-id="element-1830" data-mp="2us8bp8lsrsfj">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-1831" data-mp="2ps43e5v1m40e">Your pathway</h2>
          {items.length > 0 && <button type="button" onClick={onClear} className="text-xs font-medium text-gray-500 transition-colors hover:text-[#DB3725]" data-id="element-1832" data-mp="3ndi02bgnj0t1">
              Clear
            </button>}
        </div>
        <p className="mt-1 text-sm text-gray-600" data-id="element-1833" data-mp="38q6klyhcmwnn">
          {items.length === 0 ? 'Nothing added yet' : `${items.length} ${items.length === 1 ? 'module' : 'modules'} · ${formatHours(totalHours(items))} total`}
        </p>
      </div>

      <div className="space-y-3 border-b border-gray-100 p-5" data-id="element-1834" data-mp="3lwkqywhw1t5q">
        <label className="block" data-id="element-1835" data-mp="3lv9jallbo19c">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1836" data-mp="3qvnqy2j0h96u">
            Company
            <span aria-hidden="true" className="ml-0.5 text-[#DB3725]" data-id="element-1837" data-mp="3bz6knppoikr5">
              *
            </span>
          </span>
          <input value={company} onChange={e => onCompanyChange(e.target.value)} placeholder="e.g. Meridian Health" required aria-required="true" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1838" data-mp="3gy9esf44f6kc" />
          {companyFromNotes && <span className="mt-1 flex items-center gap-1 text-xs text-[#30718D]" data-id="element-1839" data-mp="rg0nx9kdoxoy">
              <Sparkles size={11} aria-hidden="true" data-id="element-1840" data-mp="w78xc5lpkj24" />
              From your notes — edit if that’s not right
            </span>}
        </label>

        <label className="block" data-id="element-1841" data-mp="2gw39wr823u9m">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1842" data-mp="2bykxgqutywwv">
            Pathway name
            <span aria-hidden="true" className="ml-0.5 text-[#DB3725]" data-id="element-1843" data-mp="2qnzac8xm7wh0">
              *
            </span>
          </span>
          <input value={name} onChange={e => onNameChange(e.target.value)} placeholder="e.g. New Manager Track" required aria-required="true" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 placeholder:text-gray-500 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1844" data-mp="2lppc1pglgfr6" />
        </label>

        <div data-id="element-1845" data-mp="230996vhfsb7l">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1846" data-mp="3iedevejqnwg0">
            Built for
          </span>
          <SelectDropdown ariaLabel="Audience this pathway is built for" fullWidth scrollMenu value={audience} onChange={value => onAudienceChange(value as AudienceLevel | 'any')} options={[{
          value: 'any',
          label: 'Mixed audience'
        }, ...(Object.keys(LEVEL_LABELS) as AudienceLevel[]).map(level => ({
          value: level,
          label: LEVEL_LABELS[level]
        }))]} data-id="element-1847" data-mp="3eca12j9lkfrk" />
        </div>

      </div>

      <div className="max-h-[420px] overflow-y-auto p-5" data-id="element-1848" data-mp="1xazi8iut1170">
        {items.length === 0 ? <div className="py-8 text-center" data-id="element-1849" data-mp="21arvv9z21xj8">
            <Layers size={24} className="mx-auto text-gray-400" data-id="element-1850" data-mp="262t2ogdgew1v" />
            <p className="mx-auto mt-3 max-w-[15rem] text-sm text-gray-600" data-id="element-1851" data-mp="264cmggybjp8e">
              Add modules from the results, then drag them into the order the
              prospect should take them.
            </p>
          </div> : <>
            <p className="mb-3 text-xs text-gray-500" data-id="element-1852" data-mp="163hjenc53am5">
              Drag a module card to reorder it. The arrow buttons remain available
              for keyboard and precise adjustments.
            </p>
            <Reorder.Group as="ul" axis="y" values={items} onReorder={onReorder} className="space-y-2" aria-label="Modules in pathway order" data-id="element-1853" data-mp="x1i01odnjug1">
            <AnimatePresence initial={false} data-id="element-1854" data-mp="n5y451z38vra">
              {items.map((moduleId, index) => {
              const module = moduleById(moduleId);
              if (!module) return null;
              return <Reorder.Item key={moduleId} as="li" value={moduleId} initial={{
                opacity: 0,
                y: -6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                x: 8
              }} transition={{
                duration: 0.18,
                ease: [0.23, 1, 0.32, 1]
              }} className="flex cursor-grab items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 active:cursor-grabbing" data-id="element-1855" data-mp="dchco4mfbsqs" data-mpi={index}>
                    <span className="mt-0.5 flex h-5 w-3 flex-shrink-0 items-center justify-center text-gray-400" aria-hidden="true" data-id="element-1856" data-mp="1eaiwoiv7s3t7">
                      <GripVertical size={15} data-id="element-1857" data-mp="14jo99b68ey66" />
                    </span>
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-bold text-gray-600" data-id="element-1858" data-mp="19cr3lliu2bnf">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-1859" data-mp="zmfp551iwk2a">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-1860" data-mp="lmerdygk1sr1">
                        {module.name}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500" data-id="element-1861" data-mp="qjwq6w0uyrlb">
                        {formatHours(module.durationHours)} · {module.category}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center" data-id="element-1862" data-mp="21mtragea4c1">
                      <button type="button" onPointerDown={event => event.stopPropagation()} onClick={() => onMove(moduleId, -1)} disabled={index === 0} aria-label={`Move ${module.name} earlier`} className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-1863" data-mp="gy59n8zfxchh">
                        <ArrowUp size={14} data-id="element-1864" data-mp="uoqtp10mrsgy" />
                      </button>
                      <button type="button" onPointerDown={event => event.stopPropagation()} onClick={() => onMove(moduleId, 1)} disabled={index === items.length - 1} aria-label={`Move ${module.name} later`} className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-1865" data-mp="18oiebklnk73x">
                        <ArrowDown size={14} data-id="element-1866" data-mp="1vzxtecfhhr89" />
                      </button>
                      <button type="button" onPointerDown={event => event.stopPropagation()} onClick={() => onRemove(moduleId)} aria-label={`Remove ${module.name}`} className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-[#DB3725]" data-id="element-1867" data-mp="2tmofj1xj05y4">
                        <X size={14} data-id="element-1868" data-mp="189bzdp2be7h9" />
                      </button>
                    </div>
                  </Reorder.Item>;
            })}
            </AnimatePresence>
            </Reorder.Group>
          </>}
      </div>

      <div className="border-t border-gray-100 p-5" data-id="element-1869" data-mp="3fwwvy1r7yl4n">
        <PathwayButton fullWidth onClick={onShare} disabled={!canSave} data-id="element-1870" data-mp="3jwfeiqvkik4b">
          <Share2 size={18} data-id="element-1871" data-mp="3jukqh3p02bw0" />
          Review &amp; Save
        </PathwayButton>
        <p className="mt-2 text-center text-xs text-gray-500" data-id="element-1872" data-mp="3oup84x1n9cnt">
          {blockedReason ?? 'Save it now, then combine and export it from your library.'}
        </p>
      </div>
    </aside>;
}